import { useState, useEffect, useMemo, useRef } from 'react';
import { debounce, DebouncedFunc } from 'lodash';
import { searchLineItems } from '../components/lineItem/lineitem.api';
import { LineItemDTO } from '../components/project/project.type';

export const DEFAULT_LINE_ITEM_SEARCH_MIN_LENGTH = 2;

interface UseLineItemSearchOptions {
  debounceMs?: number;
  minSearchLength?: number;
  resetOnClose?: boolean;
}

interface UseLineItemSearchReturn {
  lineItemOptions: LineItemDTO[];
  isSearching: boolean;
  searchLineItems: DebouncedFunc<(searchTerm: string) => void>;
  resetSearch: () => void;
  // Adds a newly created item to the results without waiting for a refetch.
  addCreatedOption: (created: LineItemDTO) => void;
}

export const useLineItemSearch = (isOpen: boolean, options: UseLineItemSearchOptions = {}): UseLineItemSearchReturn => {
  const { debounceMs = 500, minSearchLength = DEFAULT_LINE_ITEM_SEARCH_MIN_LENGTH, resetOnClose = true } = options;

  const [lineItemOptions, setLineItemOptions] = useState<LineItemDTO[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const latestTermRef = useRef('');

  const debouncedSearch = useMemo(
    () =>
      debounce(async (searchTerm: string) => {
        try {
          const response = await searchLineItems(searchTerm);
          if (latestTermRef.current !== searchTerm) return;
          const data = response.data || response || [];
          setLineItemOptions(Array.isArray(data) ? data : []);
        } catch (error) {
          if (latestTermRef.current === searchTerm) setLineItemOptions([]);
        } finally {
          if (latestTermRef.current === searchTerm) setIsSearching(false);
        }
      }, debounceMs),
    [debounceMs],
  );

  const search = useMemo(
    () =>
      Object.assign(
        (searchTerm: string) => {
          latestTermRef.current = searchTerm;
          if (!searchTerm || searchTerm.length < minSearchLength) {
            debouncedSearch.cancel();
            setLineItemOptions([]);
            setIsSearching(false);
            return;
          }
          setIsSearching(true);
          debouncedSearch(searchTerm);
        },
        { cancel: debouncedSearch.cancel, flush: debouncedSearch.flush },
      ) as DebouncedFunc<(searchTerm: string) => void>,
    [debouncedSearch, minSearchLength],
  );

  // Cleanup search results when dialog closes
  useEffect(() => {
    if (!isOpen && resetOnClose) {
      latestTermRef.current = '';
      debouncedSearch.cancel();
      setLineItemOptions([]);
      setIsSearching(false);
    }
  }, [isOpen, resetOnClose, debouncedSearch]);

  const resetSearch = () => {
    latestTermRef.current = '';
    debouncedSearch.cancel();
    setLineItemOptions([]);
    setIsSearching(false);
  };

  const addCreatedOption = (created: LineItemDTO) => {
    setLineItemOptions(prev => [{ ...created, isExactMatch: true }, ...prev.filter(option => option.id !== created.id)]);
  };

  return {
    lineItemOptions,
    isSearching,
    searchLineItems: search,
    resetSearch,
    addCreatedOption,
  };
};
